/** QuickTicketing — Fiche d'un ticket : description, fil, réponse, actions selon le rôle. */
import React, { useEffect, useState } from 'react';
import { api, fmtDate, ago } from '../api/client.js';
import { StatusBadge, PriorityTag } from './Badges.jsx';
import { useAttachments, AttachmentPicker, AttachmentList } from './Attachments.jsx';
import { payload } from '../attachments.js';
import RichText from './RichText.jsx';
import { DELIVERED } from '../../shared/workflow.js';

/** Actions de l'administrateur : état visé, libellé, commentaire exigé. */
const ADMIN_ACTIONS = [
  { to: 'a_analyser', label: 'À analyser' },
  { to: 'a_redevelopper', label: 'À redévelopper' },
  { to: 'en_cours', label: 'En cours' },
  { to: 'en_attente', label: 'Demander des infos', needs: 'Question posée au demandeur' },
  { to: 'a_tester', label: 'À tester', needs: 'Ce qui a été corrigé et comment le tester (lu par le demandeur)', resolution: true },
  { to: 'resolu', label: 'Résolu', needs: 'Ce qui a été corrigé (lu par le demandeur)', resolution: true },
  { to: 'rejete', label: 'Sans suite', needs: 'Raison (doublon, pas un défaut…)' },
  { to: 'clos', label: 'Clore' },
];

export default function TicketView({ id, me, meta, navigate }) {
  const [t, setT] = useState(null);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    let alive = true;
    setT(null);
    setError(null);
    api(`/tickets/${id}`).then((r) => alive && setT(r)).catch((e) => alive && setError(e.message));
    return () => { alive = false; };
  }, [id]);

  if (error) return <div className="page narrow"><div className="error-line">{error}</div><button onClick={() => navigate('/')}>← Retour</button></div>;
  if (!t) return <div className="page narrow empty"><span className="spinner" /> Chargement…</div>;

  const appName = meta.apps.find((a) => a.id === t.app)?.name ?? t.app;
  const filesOf = (ev) => t.attachments.filter((a) => ev.files?.includes(a.file));
  const isReporter = t.reporter === me.username;

  return (
    <div className="page ticket-page">
      <div className="ticket-main">
        <div className="row wrap">
          <button className="icon" onClick={() => history.length > 1 ? history.back() : navigate('/')}>← Retour</button>
          <span className="mono muted">{t.id}</span>
          <StatusBadge status={t.status} meta={meta} />
          {me.admin && <PriorityTag priority={t.priority} />}
        </div>
        <h2 className="ticket-title">{t.title}</h2>
        <div className="muted">
          {appName} · {meta.types.find((x) => x.id === t.type)?.label} · {meta.severities.find((x) => x.id === t.severity)?.label}
          {' '}· signalé par <b>{t.reporter}</b> le {fmtDate(t.createdAt)}
        </div>

        {t.resolution && [...DELIVERED, 'clos'].includes(t.status) && (
          <div className="card resolution">
            <div className="lbl">Résolution{t.resolution.version && ` — version ${t.resolution.version}`}</div>
            <RichText text={t.resolution.text} />
            {isReporter && DELIVERED.includes(t.status) && <ReporterResolved t={t} onChange={setT} />}
          </div>
        )}

        <div className="card">
          <RichText text={t.description} />
          <AttachmentList ticketId={t.id} items={filesOf(t.events[0])} onZoom={setZoom} />
        </div>

        <h3>Suivi</h3>
        <ol className="timeline">
          {t.events.slice(1).map((e) => <Event key={e.id} e={e} t={t} meta={meta} files={filesOf(e)} onZoom={setZoom} />)}
          {t.events.length === 1 && <li className="muted">Aucune activité pour l'instant.</li>}
        </ol>

        {!['clos', 'rejete'].includes(t.status) || me.admin
          ? <Composer t={t} me={me} onChange={setT} />
          : <div className="empty">Ticket {t.status === 'clos' ? 'clos' : 'classé sans suite'}. Un nouveau problème ? Ouvrez un nouveau ticket.</div>}
      </div>

      <aside className="ticket-side">
        {me.admin
          ? <AdminPanel t={t} meta={meta} onChange={setT} />
          : <ReporterPanel t={t} meta={meta} isReporter={isReporter} onChange={setT} />}
        <Context t={t} />
      </aside>

      {zoom && <div className="lightbox" onClick={() => setZoom(null)}><img src={zoom} alt="" /></div>}
    </div>
  );
}

function Event({ e, t, meta, files, onZoom }) {
  const label = (id) => meta.statuses.find((s) => s.id === id)?.label ?? id;
  if (e.kind === 'status') {
    return (
      <li className="ev ev-status">
        <span className="muted">{ago(e.at)}</span>{' '}
        {e.auto
          ? <>Réponse reçue : retour à <StatusBadge status={e.to} meta={meta} /></>
          : <><b>{e.by}</b> a passé le ticket à <StatusBadge status={e.to} meta={meta} /></>}
      </li>
    );
  }
  if (e.kind === 'field') {
    return <li className="ev ev-status internal"><span className="muted">{ago(e.at)}</span> <b>{e.by}</b> : {e.label} → {String(e.to ?? '—')}</li>;
  }
  if (e.kind === 'comment') {
    return (
      <li className={`ev ev-comment${e.internal ? ' internal' : ''}${e.by === t.reporter ? ' from-reporter' : ''}`}>
        <div className="ev-head">
          <b>{e.by}</b>
          {e.internal && <span className="tag-internal">note interne</span>}
          <span className="muted" title={fmtDate(e.at)}>{ago(e.at)}</span>
        </div>
        {e.text && <RichText text={e.text} />}
        <AttachmentList ticketId={t.id} items={files} onZoom={onZoom} />
      </li>
    );
  }
  return null;
}

function Composer({ t, me, onChange }) {
  const [text, setText] = useState('');
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const att = useAttachments();
  const waiting = t.status === 'en_attente' && t.reporter === me.username;

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      onChange(await api(`/tickets/${t.id}/comments`, { method: 'POST', body: { text, internal, attachments: payload(att.files) } }));
      setText('');
      att.clear();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`card composer${internal ? ' internal' : ''}`} onPaste={att.onPaste}>
      {waiting && <div className="notice">Une précision vous est demandée : votre réponse relance le traitement.</div>}
      <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder={internal ? 'Note interne (invisible du demandeur)…' : 'Ajouter un commentaire…'} />
      <AttachmentPicker att={att} hint="Capture : Ctrl+V, glisser, ou" />
      {error && <div className="error-line">{error}</div>}
      <div className="row">
        {me.admin && (
          <label className="check"><input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} /> Note interne</label>
        )}
        <span className="grow" />
        <button className="primary" disabled={busy || (!text.trim() && !att.files.length)} onClick={send}>
          {busy ? <span className="spinner" /> : waiting ? 'Répondre' : 'Commenter'}
        </button>
      </div>
    </div>
  );
}

/** Passage d'état avec commentaire facultatif ou exigé (et version pour une résolution). */
function StatusForm({ t, action, onDone, onCancel }) {
  const [comment, setComment] = useState('');
  const [version, setVersion] = useState('');
  const [commit, setCommit] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      onDone(await api(`/tickets/${t.id}/status`, { method: 'POST', body: { status: action.to, comment, version, commit } }));
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };
  return (
    <div className="status-form">
      <div className="lbl">{action.label}</div>
      <textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={action.needs || 'Commentaire public (facultatif)'} autoFocus />
      {action.resolution && (
        <div className="row">
          <input className="grow" placeholder="Version (ex. 1.9.2)" value={version} onChange={(e) => setVersion(e.target.value)} />
          <input className="grow" placeholder="Commit" value={commit} onChange={(e) => setCommit(e.target.value)} />
        </div>
      )}
      {error && <div className="error-line">{error}</div>}
      <div className="row">
        <span className="grow" />
        <button onClick={onCancel}>Annuler</button>
        <button className="primary" disabled={busy || (action.needs && !comment.trim())} onClick={go}>Valider</button>
      </div>
    </div>
  );
}

function AdminPanel({ t, meta, onChange }) {
  const [action, setAction] = useState(null);
  const [error, setError] = useState(null);
  const patch = async (field, value) => {
    setError(null);
    try { onChange(await api(`/tickets/${t.id}`, { method: 'PATCH', body: { [field]: value || null } })); } catch (e) { setError(e.message); }
  };
  return (
    <div className="card side-card">
      <h3>Traitement</h3>
      {action ? (
        <StatusForm t={t} action={action} onCancel={() => setAction(null)} onDone={(r) => { onChange(r); setAction(null); }} />
      ) : (
        <div className="actions">
          {ADMIN_ACTIONS.filter((a) => a.to !== t.status).map((a) => (
            <button key={a.to} className={`st-btn st-${a.to}`} onClick={() => setAction(a)}>{a.label}</button>
          ))}
        </div>
      )}
      <h3>Qualification</h3>
      <div className="fields">
        <label>Priorité
          <select value={t.priority || ''} onChange={(e) => patch('priority', e.target.value)}>
            <option value="">— non fixée —</option>
            {meta.priorities.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </label>
        <label>Application
          <select value={t.app} onChange={(e) => patch('app', e.target.value)}>
            {meta.apps.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </label>
        <label>Nature
          <select value={t.type} onChange={(e) => patch('type', e.target.value)}>
            {meta.types.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
          </select>
        </label>
        <label>Gravité
          <select value={t.severity} onChange={(e) => patch('severity', e.target.value)}>
            {meta.severities.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
          </select>
        </label>
      </div>
      {error && <div className="error-line">{error}</div>}
    </div>
  );
}

function ReporterPanel({ t, meta, isReporter, onChange }) {
  const [action, setAction] = useState(null);
  const s = meta.statuses.find((x) => x.id === t.status);
  return (
    <div className="card side-card">
      <h3>Où en est-on ?</h3>
      <StatusBadge status={t.status} meta={meta} />
      <p className="muted hint">{s?.hint}</p>
      {isReporter && ['nouveau', 'en_attente'].includes(t.status) && (action
        ? <StatusForm t={t} action={action} onCancel={() => setAction(null)} onDone={(r) => { onChange(r); setAction(null); }} />
        : <button className="danger" onClick={() => setAction({ to: 'rejete', label: 'Retirer le ticket' })}>Retirer le ticket</button>)}
    </div>
  );
}

function ReporterResolved({ t, onChange }) {
  const [reopen, setReopen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const confirm = async () => {
    setBusy(true);
    setError(null);
    try { onChange(await api(`/tickets/${t.id}/status`, { method: 'POST', body: { status: 'clos' } })); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  if (reopen) {
    return <StatusForm t={t} action={{ to: 'a_analyser', label: 'Rouvrir le ticket', needs: 'Qu\'est-ce qui ne va toujours pas ?' }} onCancel={() => setReopen(false)} onDone={onChange} />;
  }
  return (
    <div className="row">
      <span className="muted grow">Le problème est-il réglé de votre côté ?</span>
      <button onClick={() => setReopen(true)}>Non, rouvrir</button>
      <button className="primary" disabled={busy} onClick={confirm}>✓ Oui, c'est réglé</button>
      {error && <div className="error-line">{error}</div>}
    </div>
  );
}

function Context({ t }) {
  const c = t.context || {};
  if (!c.url && !c.version && !c.userAgent) return null;
  return (
    <div className="card side-card context">
      <h3>Contexte</h3>
      {c.url && <div><span className="lbl">Page</span><a href={c.url} target="_blank" rel="noreferrer noopener" className="mono">{c.url}</a></div>}
      {c.version && <div><span className="lbl">Version</span><span className="mono">{c.version}</span></div>}
      {c.userAgent && <div><span className="lbl">Navigateur</span><span className="mono small">{c.userAgent}</span></div>}
    </div>
  );
}
